import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MODULE_GROUPS, ROLE_VISIBILITY } from "@/lib/modules";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import {
  ChevronsLeft, ChevronsRight, ChevronDown, Search, Sparkles, Settings, LogOut,
} from "lucide-react";

export function Sidebar({ user }) {
  const [collapsed, setCollapsed] = useState(false);
  const [query, setQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const isSuper = (user?.roles || []).includes("super_admin");

  // Role-based visibility (unchanged).
  const visibility =
    (user?.roles || []).some((r) => ROLE_VISIBILITY[r] === "*")
      ? "*"
      : new Set((user?.roles || []).flatMap((r) => ROLE_VISIBILITY[r] || []));
  const isVisible = (key) =>
    visibility === "*" || (visibility instanceof Set && visibility.has(key));

  // Split each group into live items (have a real page) and "coming soon" items.
  // The coming-soon items all collapse into a single accordion at the bottom so
  // they don't compete for attention with the live navigation.
  const { groupsLive, comingSoon, hasActiveByGroup } = useMemo(() => {
    const live = [];
    const soon = [];
    const active = {};
    for (const g of MODULE_GROUPS) {
      const liveItems = [];
      for (const item of g.items) {
        if (!isVisible(item.key)) continue;
        if (item.requireRoles && !item.requireRoles.some((r) => (user?.roles || []).includes(r))) continue;
        if (item.enabled) liveItems.push(item);
        else soon.push({ ...item, group: g.label });
        if (location.pathname.startsWith(item.path)) active[g.label] = true;
      }
      if (liveItems.length) live.push({ ...g, items: liveItems });
    }
    return { groupsLive: live, comingSoon: soon, hasActiveByGroup: active };
  }, [location.pathname, user]);

  // Default-expanded set: first group + Campus + any group containing the active route.
  const initialOpen = useMemo(() => {
    const open = new Set();
    if (groupsLive[0]) open.add(groupsLive[0].label);
    open.add("Campus");
    Object.keys(hasActiveByGroup).forEach((l) => open.add(l));
    return open;
  }, [groupsLive, hasActiveByGroup]);
  const [openGroups, setOpenGroups] = useState(initialOpen);
  const isOpen = (label) => openGroups.has(label);
  const toggleGroup = (label) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  // Local sidebar filter — when the user types here, all groups expand and
  // we only show items whose label matches.
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return groupsLive;
    return groupsLive
      .map((g) => ({ ...g, items: g.items.filter((i) => i.label.toLowerCase().includes(q)) }))
      .filter((g) => g.items.length);
  }, [q, groupsLive]);

  return (
    <aside
      className={cn(
        "h-[calc(100vh-1.5rem)] sticky top-3 self-start rounded-2xl shadow-xl bg-[hsl(var(--sidebar-bg))] text-[hsl(var(--sidebar-fg))] flex flex-col transition-[width] duration-200 overflow-hidden",
        collapsed ? "w-[68px]" : "w-[232px]"
      )}
      data-testid="app-sidebar"
    >
      {/* Brand — Kampus 365 for super admins, tenant crest for everyone else */}
      <div className="h-20 flex items-center px-3 border-b border-[hsl(var(--sidebar-fg)/0.10)]">
        <Link to="/dashboard" className="flex items-center gap-2.5 min-w-0" data-testid="brand-link">
          {isSuper ? (
            <>
              <div className="h-12 w-12 rounded-xl bg-[hsl(var(--sidebar-fg)/0.10)] border border-[hsl(var(--sidebar-fg)/0.18)] flex items-center justify-center shrink-0">
                <Sparkles className="h-6 w-6" />
              </div>
              {!collapsed && (
                <div className="font-display font-extrabold tracking-tight text-[15px] leading-tight" data-testid="brand-super">
                  Kampus 365<br />
                  <span className="text-[9px] font-semibold tracking-[0.18em] opacity-70">PLATFORM CONSOLE</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-600 to-blue-700 flex items-center justify-center shrink-0 shadow-sm">
                <span className="font-display font-extrabold text-white text-[15px] tracking-tight">K365</span>
              </div>
              {!collapsed && (
                <div className="font-display font-extrabold tracking-tight text-[15px] leading-tight">
                  Kampus 365<br />
                  <span className="text-[9px] font-semibold tracking-[0.18em] opacity-70">EDUCATION OS</span>
                </div>
              )}
            </>
          )}
        </Link>
      </div>

      {/* Local search */}
      {!collapsed && (
        <div className="px-3 pt-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[hsl(var(--sidebar-fg)/0.5)]" />
            <input
              data-testid="sidebar-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search menu…"
              className="w-full pl-8 pr-2 h-8 rounded-lg bg-[hsl(var(--sidebar-fg)/0.08)] border border-transparent focus:border-[hsl(var(--sidebar-fg)/0.22)] focus:outline-none text-[13px] placeholder:text-[hsl(var(--sidebar-fg)/0.4)]"
            />
          </div>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto sidebar-scroll py-3">
        {filtered.map((group) => {
          const isFlat = !!group.flat;
          const open = isFlat || !!q || isOpen(group.label);
          return (
            <div key={group.label} className="px-2 mb-1">
              {!collapsed && !isFlat && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.label)}
                  className="w-full flex items-center justify-between px-2 py-1.5 rounded-md text-[10px] uppercase tracking-[0.16em] font-semibold text-[hsl(var(--sidebar-fg)/0.55)] hover:text-[hsl(var(--sidebar-fg))] transition-colors"
                  data-testid={`sidebar-group-${slugify(group.label)}`}
                  aria-expanded={open}
                >
                  <span>{group.label}</span>
                  <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open ? "rotate-0" : "-rotate-90")} />
                </button>
              )}
              {(open || collapsed) && (
                <ul className="space-y-0.5 mt-0.5">
                  {group.items.map((item) => <NavItem key={item.key} item={item} collapsed={collapsed} location={location} />)}
                </ul>
              )}
            </div>
          );
        })}

        {/* Coming soon accordion */}
        {!collapsed && !q && comingSoon.length > 0 && (
          <ComingSoon items={comingSoon} />
        )}

        {!collapsed && q && filtered.length === 0 && (
          <div className="px-4 py-6 text-center text-[12px] text-[hsl(var(--sidebar-fg)/0.55)]">
            No menu matches “{q}”.
          </div>
        )}
      </nav>

      {/* Pinned footer — Settings + Logout (visual match to reference) */}
      <div className="px-2 pb-2 border-t border-[hsl(var(--sidebar-fg)/0.10)] pt-2 space-y-0.5">
        <button
          type="button"
          onClick={() => navigate("/mfa")}
          className={cn(
            "w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[13px] hover:bg-[hsl(var(--sidebar-fg)/0.08)] transition-colors",
            collapsed && "justify-center"
          )}
          data-testid="sidebar-settings"
          title="Settings"
        >
          <Settings className="h-4 w-4 shrink-0" />
          {!collapsed && <span className="truncate flex-1 text-left">Settings</span>}
        </button>
        <button
          type="button"
          onClick={logout}
          className={cn(
            "w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[13px] hover:bg-[hsl(var(--sidebar-fg)/0.08)] transition-colors",
            collapsed && "justify-center"
          )}
          data-testid="sidebar-logout"
          title="Logout"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span className="truncate flex-1 text-left">Logout</span>}
        </button>
      </div>

      <button
        onClick={() => setCollapsed((c) => !c)}
        className="h-9 border-t border-[hsl(var(--sidebar-fg)/0.10)] flex items-center justify-center text-[hsl(var(--sidebar-fg)/0.6)] hover:text-[hsl(var(--sidebar-fg))] hover:bg-[hsl(var(--sidebar-fg)/0.06)] transition-colors"
        data-testid="sidebar-collapse-toggle"
      >
        {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
      </button>
    </aside>
  );
}

function NavItem({ item, collapsed, location }) {
  const Icon = item.icon;
  const active = location.pathname.startsWith(item.path);
  const content = (
    <div
      className={cn(
        "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] transition-all",
        active && "bg-white text-slate-900 font-semibold shadow-md",
        !active && "hover:bg-[hsl(var(--sidebar-fg)/0.08)] text-[hsl(var(--sidebar-fg)/0.85)]",
        collapsed && "justify-center"
      )}
      data-testid={`sidebar-nav-${item.key}`}
    >
      <Icon className={cn("h-4 w-4 shrink-0", active && "text-primary")} />
      {!collapsed && <span className="truncate flex-1">{item.label}</span>}
    </div>
  );
  return (
    <li>
      <Link to={item.path}>{content}</Link>
    </li>
  );
}

function ComingSoon({ items }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="px-2 mt-4 border-t border-[hsl(var(--sidebar-fg)/0.08)] pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-2 py-1.5 rounded-md text-[10px] uppercase tracking-[0.16em] font-semibold text-[hsl(var(--sidebar-fg)/0.5)] hover:text-[hsl(var(--sidebar-fg)/0.75)]"
        data-testid="sidebar-coming-soon-toggle"
      >
        <span className="flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> Coming soon · {items.length}
        </span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open ? "rotate-0" : "-rotate-90")} />
      </button>
      {open && (
        <ul className="space-y-0.5 mt-1">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.key}>
                <span
                  title={`Coming in Sprint ${item.sprint || "—"}`}
                  className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] text-[hsl(var(--sidebar-fg)/0.55)] cursor-not-allowed"
                  data-testid={`sidebar-soon-${item.key}`}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate flex-1">{item.label}</span>
                  {item.sprint && (
                    <span className="text-[9px] font-mono px-1 py-0 rounded bg-[hsl(var(--sidebar-fg)/0.08)]">S{item.sprint}</span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
